import { useState, useRef } from "react";
import { B, ADMIN_API } from "../../design/tokens";
import Reveal from "../shared/Reveal";
import Tag from "../shared/Tag";
import Icon from "../shared/Icon";

const CONTACT_API = `${ADMIN_API}/contact`;
const EMAIL_DOMAINS = ["gmail.com","yahoo.com","outlook.com","hotmail.com","icloud.com","live.com","ymail.com","protonmail.com"];

export default function ContactSection() {
  const [form, setForm]           = useState({ name: "", phone: "", email: "", service: "", message: "" });
  const [consent, setConsent]     = useState(false);
  const [sent, setSent]           = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formErr, setFormErr]     = useState("");
  const [emailSuggestions, setEmailSuggestions] = useState([]);
  const emailRef = useRef(null);

  const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());
  const isValidPhone = (v) => {
    const d = v.replace(/\D/g, "");
    return /^(09\d{9}|639\d{9})$/.test(d);
  };
  const formatPhone = (v) => {
    const d = v.replace(/\D/g, "");
    if (/^639\d{9}$/.test(d)) return "+63" + d.slice(2);
    if (/^09\d{9}$/.test(d)) return "+63" + d.slice(1);
    return v;
  };

  const handlePhoneChange = (e) => {
    const digits = e.target.value.replace(/\D/g, "").slice(0, 11);
    let out = digits;
    if (digits.length > 7)      out = digits.slice(0, 4) + "-" + digits.slice(4, 7) + "-" + digits.slice(7);
    else if (digits.length > 4) out = digits.slice(0, 4) + "-" + digits.slice(4);
    setForm(f => ({ ...f, phone: out }));
  };

  const handleEmailChange = (e) => {
    const val = e.target.value;
    setForm(f => ({ ...f, email: val }));
    const atIdx = val.indexOf("@");
    if (atIdx !== -1) {
      const afterAt = val.slice(atIdx + 1).toLowerCase();
      const prefix  = val.slice(0, atIdx);
      const matches = EMAIL_DOMAINS.filter(d => d.startsWith(afterAt) && d !== afterAt);
      setEmailSuggestions(matches.map(d => `${prefix}@${d}`));
    } else {
      setEmailSuggestions([]);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim())               { setFormErr("Full name is required."); return; }
    if (!form.phone.trim())              { setFormErr("Mobile number is required."); return; }
    if (!isValidPhone(form.phone))       { setFormErr("Enter a valid PH mobile number (e.g. 0917-123-4567)."); return; }
    if (form.email && !isValidEmail(form.email)) { setFormErr("Please enter a valid email address."); return; }
    if (!consent)                        { setFormErr("Please tick the consent checkbox to proceed."); return; }
    setFormErr(""); setSubmitting(true);
    const payload = { ...form, phone: formatPhone(form.phone) };
    try {
      const res  = await fetch(CONTACT_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) { setFormErr(data.error || "Something went wrong. Please try again."); return; }
      setSent(true);
      window.gtag?.('event', 'generate_lead', { event_category: 'inquiry', event_label: 'contact_form' });
    } catch {
      setFormErr("Could not send your inquiry. Please try again or email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  const inp = { width: "100%", padding: "13px 16px", background: B.ghost, border: `1.5px solid ${B.pale}`, color: B.navy, fontFamily: "'Space Grotesk', sans-serif", fontSize: 15, outline: "none", transition: "border-color 0.25s, background 0.25s", boxSizing: "border-box" };
  const lbl = { display: "block", fontFamily: "'Space Grotesk', sans-serif", fontSize: 11, color: B.azure, marginBottom: 8, fontWeight: 600, letterSpacing: 2, textTransform: "uppercase" };
  const req = { color: "#DC2626", marginLeft: 3 };

  return (
    <section id="contact" style={{ background: B.white, padding: "120px 0", borderTop: `1px solid ${B.pale}` }}>
      <div style={{ maxWidth: 1280, margin: "0 auto", padding: "0 32px" }}>
        <div style={{ display: "grid", gridTemplateColumns: "480px 1fr", gap: 80, alignItems: "start" }} className="contact-grid">
          <Reveal from="left">
            <Tag text="Get In Touch" />
            <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(38px, 4.8vw, 60px)", fontWeight: 700, color: B.navy, lineHeight: 1.0, letterSpacing: -1, marginBottom: 28 }}>
              Let's Discuss<br />Your Property<br /><em style={{ color: B.azure }}>Needs.</em>
            </h2>
            <p style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 15.5, color: B.slate, lineHeight: 1.9, maxWidth: 420, marginBottom: 44 }}>
              Whether you have a simple RPT inquiry or need a large-scale environmental planning engagement — reach out and we'll assess your situation at no cost.
            </p>
            {[
              { icon: "mail",  label: "Email",           value: "inquiry@neswcorp.com" },
              { icon: "phone", label: "Mobile",          value: "0998-859-0597" },
              { icon: "clock", label: "Business Hours",  value: "Monday – Saturday, 8:00 AM – 5:00 PM" },
            ].map((c, i) => (
              <div key={i} style={{ display: "flex", gap: 16, marginBottom: 22, alignItems: "flex-start" }}>
                <div style={{ width: 44, height: 44, background: `${B.azure}10`, border: `1.5px solid ${B.azure}22`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Icon name={c.icon} size={20} color={B.azure} strokeWidth={1.6} /></div>
                <div>
                  <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 10.5, color: B.azure, letterSpacing: 2, textTransform: "uppercase", fontWeight: 600, marginBottom: 4 }}>{c.label}</div>
                  <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 14.5, color: B.navy, lineHeight: 1.65, whiteSpace: "pre-line" }}>{c.value}</div>
                </div>
              </div>
            ))}
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 12, padding: "14px 18px", background: `${B.success}0d`, border: `1px solid ${B.success}22` }}>
              <span style={{ width: 8, height: 8, borderRadius: "50%", background: B.success, boxShadow: `0 0 8px ${B.success}`, flexShrink: 0 }} />
              <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 13.5, color: "#16A34A", fontWeight: 600 }}>Typical response within 24 business hours</span>
            </div>
          </Reveal>

          <Reveal from="right" delay={0.12}>
            {!sent ? (
              <div style={{ background: B.ghost, border: `1.5px solid ${B.pale}`, padding: "44px 40px", boxShadow: `0 8px 40px ${B.navy}08` }}>

                {/* Row 1: Name */}
                <div style={{ marginBottom: 20 }}>
                  <label style={lbl}>Full Name <span style={req}>*</span></label>
                  <input type="text" placeholder="e.g. Juan dela Cruz" value={form.name}
                    onChange={e => setForm({ ...form, name: e.target.value })} style={inp}
                    onFocus={e => { e.target.style.borderColor = B.azure; e.target.style.background = B.white; }}
                    onBlur={e => { e.target.style.borderColor = B.pale; e.target.style.background = B.ghost; }} />
                </div>

                {/* Row 2: Mobile + Service */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 20 }} className="form-grid">
                  <div>
                    <label style={lbl}>Mobile Number <span style={req}>*</span></label>
                    <input type="tel" placeholder="e.g. 0917-123-4567" value={form.phone}
                      onChange={handlePhoneChange} style={inp}
                      onFocus={e => { e.target.style.borderColor = B.azure; e.target.style.background = B.white; }}
                      onBlur={e => { e.target.style.borderColor = B.pale; e.target.style.background = B.ghost; }} />
                  </div>
                  <div style={{ position: "relative" }}>
                    <label style={lbl}>Email Address <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 10, color: B.slate, fontWeight: 400, letterSpacing: 0, textTransform: "none", marginLeft: 4 }}>(optional)</span></label>
                    <input ref={emailRef} type="text" placeholder="e.g. juan@email.com" value={form.email}
                      onChange={handleEmailChange} style={inp}
                      onFocus={e => { e.target.style.borderColor = B.azure; e.target.style.background = B.white; handleEmailChange(e); }}
                      onBlur={e => { e.target.style.borderColor = B.pale; e.target.style.background = B.ghost; setTimeout(() => setEmailSuggestions([]), 150); }} />
                    {emailSuggestions.length > 0 && (
                      <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: B.white, border: `1.5px solid ${B.azure}55`, zIndex: 20, boxShadow: `0 8px 24px ${B.navy}14` }}>
                        {emailSuggestions.map(s => (
                          <div key={s} onMouseDown={() => { setForm(f => ({ ...f, email: s })); setEmailSuggestions([]); }}
                            style={{ padding: "11px 16px", fontFamily: "'Space Grotesk', sans-serif", fontSize: 14, color: B.navy, cursor: "pointer", borderBottom: `1px solid ${B.pale}`, transition: "background 0.15s" }}
                            onMouseEnter={e => e.currentTarget.style.background = `${B.azure}0d`}
                            onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                            {s}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Row 3: Service */}
                <div style={{ marginBottom: 20 }}>
                  <label style={lbl}>Service Needed <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 10, color: B.slate, fontWeight: 400, letterSpacing: 0, textTransform: "none", marginLeft: 4 }}>(optional)</span></label>
                  <select value={form.service} onChange={e => setForm({ ...form, service: e.target.value })}
                    style={{ ...inp, appearance: "none", cursor: "pointer" }}
                    onFocus={e => { e.target.style.borderColor = B.azure; e.target.style.background = B.white; }}
                    onBlur={e => { e.target.style.borderColor = B.pale; e.target.style.background = B.ghost; }}>
                    <option value="">Select a service</option>
                    {["Real Estate Brokerage","Title Transfer","Property Appraisal","RPT Assistance","Special Purpose Acquisition","Property Management","Environmental Planning","EIA","Other"].map(s => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>

                {/* Row 4: Message */}
                <div style={{ marginBottom: 24 }}>
                  <label style={lbl}>Message <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 10, color: B.slate, fontWeight: 400, letterSpacing: 0, textTransform: "none", marginLeft: 4 }}>(optional)</span></label>
                  <textarea placeholder="Describe your property needs, location, and timeline…"
                    value={form.message} onChange={e => setForm({ ...form, message: e.target.value })}
                    rows={4} style={{ ...inp, resize: "vertical" }}
                    onFocus={e => { e.target.style.borderColor = B.azure; e.target.style.background = B.white; }}
                    onBlur={e => { e.target.style.borderColor = B.pale; e.target.style.background = B.ghost; }} />
                </div>

                {/* Consent */}
                <div
                  onClick={() => setConsent(c => !c)}
                  style={{ display: "flex", gap: 12, alignItems: "flex-start", marginBottom: 24, padding: "14px 16px", background: consent ? `${B.azure}08` : B.ghost, border: `1.5px solid ${consent ? B.azure + "44" : B.pale}`, cursor: "pointer", transition: "all 0.2s", userSelect: "none" }}
                >
                  <div style={{ width: 20, height: 20, flexShrink: 0, marginTop: 1, border: `2px solid ${consent ? B.azure : B.slate + "66"}`, background: consent ? B.azure : "transparent", display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.2s" }}>
                    {consent && <svg width="11" height="9" viewBox="0 0 12 10" fill="none"><polyline points="1,5 4.5,8.5 11,1" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                  </div>
                  <p style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 13, color: B.slate, lineHeight: 1.7, margin: 0 }}>
                    I consent to <strong style={{ color: B.navy }}>NESW Property &amp; Planning Consultancy OPC</strong> collecting and using my personal information (name, mobile, email) solely to respond to my inquiry, in accordance with the{" "}
                    <a href="https://privacy.gov.ph/data-privacy-act/" target="_blank" rel="noopener noreferrer"
                      onClick={e => e.stopPropagation()}
                      style={{ color: B.azure, fontWeight: 600, textDecoration: "none" }}>
                      Philippine Data Privacy Act of 2012 (R.A. 10173)
                    </a>.
                  </p>
                </div>

                {formErr && (
                  <div style={{ marginBottom: 16, padding: "12px 16px", background: "#FEF2F2", border: "1.5px solid #FECACA", color: "#DC2626", fontFamily: "'Space Grotesk', sans-serif", fontSize: 13.5 }}>{formErr}</div>
                )}

                <button onClick={submit} disabled={submitting}
                  style={{ width: "100%", padding: "16px", background: submitting ? B.slate : `linear-gradient(135deg, ${B.azure} 0%, ${B.royal} 100%)`, color: B.white, border: "none", fontFamily: "'Space Grotesk', sans-serif", fontSize: 15, fontWeight: 700, letterSpacing: 0.5, cursor: submitting ? "not-allowed" : "pointer", transition: "all 0.3s", boxShadow: `0 4px 24px ${B.azure}33` }}
                  onMouseEnter={e => { if (!submitting) { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = `0 8px 32px ${B.azure}44`; } }}
                  onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = `0 4px 24px ${B.azure}33`; }}
                >{submitting ? "Sending…" : "Submit Inquiry →"}</button>
              </div>
            ) : (
              <div style={{ background: B.ghost, border: `1.5px solid ${B.pale}`, padding: "80px 40px", textAlign: "center" }}>
                <div style={{ width: 70, height: 70, margin: "0 auto 24px", background: `${B.success}14`, border: `2px solid ${B.success}44`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 30, color: B.success }}>✓</div>
                <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 30, fontWeight: 700, color: B.navy, marginBottom: 12 }}>Inquiry Received!</h3>
                <p style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 15, color: B.slate, lineHeight: 1.8, maxWidth: 380, margin: "0 auto 28px" }}>Thank you for reaching out. We'll review your message and respond within 24 business hours.</p>
                <button onClick={() => { setSent(false); setConsent(false); setForm({ name: "", phone: "", email: "", service: "", message: "" }); }}
                  style={{ background: "transparent", color: B.azure, border: `1.5px solid ${B.azure}44`, padding: "11px 28px", cursor: "pointer", fontFamily: "'Space Grotesk', sans-serif", fontSize: 14, fontWeight: 600, transition: "all 0.25s" }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = B.azure; e.currentTarget.style.background = `${B.azure}08`; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = `${B.azure}44`; e.currentTarget.style.background = "transparent"; }}
                >Send Another Inquiry</button>
              </div>
            )}
          </Reveal>
        </div>
      </div>
    </section>
  );
}
